/**
 * Gmail API client minimal pour la PWA.
 *
 * Auth : Bearer access_token Google obtenu via auth/oauth.ts::getAccessToken.
 * Le token est refresh auto en cas d'expiration (45min TTL chez Google).
 *
 * Quotas Gmail v1 (par user/seconde, pas par compte) :
 *   - threads.list      :   5 units (avec query) / 1 unit (sans query)
 *   - threads.get METADATA: 10 units
 *   - 250 units/sec max
 * → Pour 20 threads : 1 + 20*10 = 201 units, en dessous du plafond.
 */

import { getAccessToken } from "../auth/oauth";

const GMAIL_API = "https://gmail.googleapis.com/gmail/v1/users/me";

/** Erreur typée pour distinguer "scope manquant" du reste. */
export class GmailScopeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GmailScopeError";
  }
}

/** Helper auth + fetch avec gestion 401/403 typée. */
async function gmailFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new Error("Not signed in");

  const res = await fetch(`${GMAIL_API}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      "Authorization": `Bearer ${token}`,
      "Accept": "application/json",
    },
  });

  if (res.status === 401) {
    throw new Error("Gmail API 401 Unauthorized — token invalide");
  }
  if (res.status === 403) {
    const body = await res.text();
    // 403 le plus souvent = scope insuffisant (gmail.readonly absent)
    if (body.includes("insufficient") || body.includes("scope") || body.includes("ACCESS_TOKEN_SCOPE_INSUFFICIENT")) {
      throw new GmailScopeError(
        "Scope Gmail manquant — re-sign-in nécessaire pour accorder l'accès à tes mails",
      );
    }
    throw new Error(`Gmail API 403: ${body.slice(0, 200)}`);
  }
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Gmail API ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

// ─── threads.list ────────────────────────────────────────────────────────────

export interface ThreadStub {
  id: string;
  snippet: string;
  historyId: string;
}

export interface ThreadsListResponse {
  threads?: ThreadStub[];
  nextPageToken?: string;
  resultSizeEstimate: number;
}

/**
 * Liste les thread IDs pour un label donné (default : INBOX).
 * Renvoie juste les IDs + snippets — pour avoir from/subject/date,
 * il faut threads.get sur chacun.
 */
export async function listThreads(opts: {
  labelIds?: string[];
  query?: string;
  maxResults?: number;
  pageToken?: string;
} = {}): Promise<ThreadsListResponse> {
  const params = new URLSearchParams();
  for (const id of opts.labelIds ?? ["INBOX"]) {
    params.append("labelIds", id);
  }
  if (opts.query) params.set("q", opts.query);
  params.set("maxResults", String(opts.maxResults ?? 20));
  if (opts.pageToken) params.set("pageToken", opts.pageToken);

  return gmailFetch<ThreadsListResponse>(`/threads?${params}`);
}

// ─── threads.get ─────────────────────────────────────────────────────────────

export interface GmailHeader {
  name: string;
  value: string;
}

export interface GmailMessagePayload {
  partId?: string;
  mimeType?: string;
  filename?: string;
  headers?: GmailHeader[];
  body?: { size: number; data?: string; attachmentId?: string };
  parts?: GmailMessagePayload[];
}

export interface GmailMessage {
  id: string;
  threadId: string;
  snippet: string;
  /** Unix ms en string. */
  internalDate: string;
  labelIds: string[];
  payload?: GmailMessagePayload;
}

export interface GmailThread {
  id: string;
  snippet: string;
  historyId: string;
  messages: GmailMessage[];
}

/**
 * Récupère un thread complet ou métadonnées.
 *
 * - format=METADATA + metadataHeaders=From,Subject,Date,To :
 *   suffit pour le rendu inbox (pas de body).
 * - format=FULL : body inclus, pour le ThreadView (Phase 4b).
 */
export async function getThread(
  id: string,
  opts: {
    format?: "minimal" | "metadata" | "full";
    metadataHeaders?: string[];
  } = {},
): Promise<GmailThread> {
  const params = new URLSearchParams();
  params.set("format", opts.format ?? "metadata");
  for (const h of opts.metadataHeaders ?? ["From", "Subject", "Date"]) {
    params.append("metadataHeaders", h);
  }
  return gmailFetch<GmailThread>(`/threads/${id}?${params}`);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// `getHeader` et `parseFrom` sont purs : extraits dans `./headers` pour
// pouvoir être importés depuis le service worker (qui ne peut pas tirer
// `getAccessToken` et ses dépendances Svelte). Re-exportés ici pour ne
// pas casser les imports historiques (`from "./api"`).
export { getHeader, parseFrom } from "./headers";

/** True si le thread contient au moins un message non lu (UNREAD label). */
export function isThreadUnread(thread: GmailThread): boolean {
  return thread.messages.some((m) => m.labelIds?.includes("UNREAD"));
}

// ─── attachments.get ─────────────────────────────────────────────────────────

export interface GmailAttachmentData {
  size: number;
  /** base64url encoded body */
  data: string;
}

/**
 * Fetch les bytes d'une pièce jointe. Quota Gmail : 5 units.
 * On appelle uniquement quand l'user tap pour télécharger/preview.
 */
export async function getAttachment(
  messageId: string,
  attachmentId: string,
): Promise<GmailAttachmentData> {
  return gmailFetch<GmailAttachmentData>(
    `/messages/${messageId}/attachments/${attachmentId}`,
  );
}

// ─── messages.send (Phase 5) ─────────────────────────────────────────────────

export interface SendMessageResult {
  id: string;
  threadId: string;
  labelIds?: string[];
}

/**
 * Envoie un message. `rawBase64Url` = RFC 822 message complet, encodé base64url.
 * Si `threadId` est fourni, Gmail attache le message au thread (= reply correct).
 * Quota : 100 units (lourd, mais on appelle 1× par send).
 */
export async function sendMessage(
  rawBase64Url: string,
  threadId?: string,
): Promise<SendMessageResult> {
  return gmailFetch<SendMessageResult>(`/messages/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      raw: rawBase64Url,
      ...(threadId ? { threadId } : {}),
    }),
  });
}

// ─── threads.modify (Phase B — read/unread + archive) ─────────────────────

export interface ModifyThreadBody {
  addLabelIds?: string[];
  removeLabelIds?: string[];
}

/**
 * Add/remove labels sur un thread. Quota 5 units. Utilisé par :
 *   - markRead     : removeLabelIds=["UNREAD"]
 *   - markUnread   : addLabelIds=["UNREAD"]
 *   - archive      : removeLabelIds=["INBOX"]
 *   - moveToInbox  : addLabelIds=["INBOX"]  (undo archive)
 *
 * Le retour contient le thread updated avec ses labels actuels — utile
 * pour réconcilier l'optimistic update si le serveur a divergé.
 */
export async function modifyThread(
  threadId: string,
  body: ModifyThreadBody,
): Promise<{ id: string; messages?: GmailMessage[] }> {
  return gmailFetch<{ id: string; messages?: GmailMessage[] }>(
    `/threads/${threadId}/modify`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

// ─── labels.list (Phase C — bottom tab bar splits) ────────────────────────

export interface GmailLabel {
  id: string;
  name: string;
  type: "system" | "user";
  /** Couleur Gmail web (si user a customisé) */
  color?: { textColor?: string; backgroundColor?: string };
  messageListVisibility?: "show" | "hide";
  labelListVisibility?: "labelShow" | "labelShowIfUnread" | "labelHide";
}

interface LabelsListResponse {
  labels?: GmailLabel[];
}

/** Liste tous les labels de l'utilisateur (system + user). Quota 1 unit. */
export async function listLabels(): Promise<GmailLabel[]> {
  const r = await gmailFetch<LabelsListResponse>(`/labels`);
  return r.labels ?? [];
}

// ─── settings.sendAs.list (pour récupérer la signature) ────────────────────

export interface SendAsConfig {
  sendAsEmail: string;
  displayName?: string;
  /** Signature HTML telle que configurée dans Gmail web. */
  signature?: string;
  isDefault?: boolean;
  isPrimary?: boolean;
  replyToAddress?: string;
}

export interface SendAsListResponse {
  sendAs?: SendAsConfig[];
}

export async function listSendAs(): Promise<SendAsConfig[]> {
  const r = await gmailFetch<SendAsListResponse>(`/settings/sendAs`);
  return r.sendAs ?? [];
}
